import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { IonContent, IonHeader, IonIcon, IonTitle, IonToolbar, LoadingController, ModalController, ActionSheetController } from '@ionic/angular/standalone';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { DisplayService, ImageUtil } from '@qid/core';
import { SexyModule } from '@qid/sexy';
import { PinchZoomComponent } from '@meddv/ngx-pinch-zoom';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';


export type PlatformType = 'web-desktop' | 'web-mobile' | 'native-mobile';

@Component({
    selector: 'image-viewer',
    templateUrl: './image-viewer.component.html',
    styleUrls: ['./image-viewer.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        SexyModule,
        IonHeader,
        IonToolbar,
        IonTitle,
        IonContent,
        IonIcon,
        PinchZoomComponent
    ],
})
export class ImageViewerComponent {

    @ViewChild('img', { static: false }) img: ElementRef<HTMLDivElement>;
    @ViewChild('mainImage') mainImage: ElementRef
    @ViewChild('zoomResult') zoomResult: ElementRef
    @ViewChild('imageContainer') imageContainer: ElementRef<HTMLDivElement>;
    @Input() imageUrl: string = '';
    @Input() imageUrlArray: string[] = [];
    @Input() autoBase: boolean = true;
    @Input() zoomType: 'pinch' | 'mouse' = 'mouse' // Default to mouse, auto-detected based on touch capability
    @Input() initialImageIndex: number = 0;
    @Input() userName: string = '';
    @Input() sidName: string = '';
    @Input() photoName: string = '';
    @Input() disableDownload = false;
    @Input() disableContextMenu = false;

    currentImageIndex: number = 0;
    private originalStatusBarStyle: Style | null = null;
    private originalStatusBarColor: string | null = null;
    private statusBarSet: boolean = false;
    private removeContextMenuListener?: () => void;
    private removeMouseDownListener?: () => void;

    constructor(private modalController: ModalController,
        private loadingController: LoadingController,
        private displayService: DisplayService,
        private actionSheetController: ActionSheetController) {
    }


    ionViewDidEnter() {
        // Ensure we have the correct image
        if (this.imageUrlArray && this.imageUrlArray.length > 0) {
            this.imageUrl = this.imageUrlArray[this.currentImageIndex];
        }

        // Set status bar to black for native devices
        this.setStatusBarBlack();

        if (this.disableContextMenu) {
            this.bindGlobalContextMenuBlockers();
        }
    }


    ngOnInit() {
        // Auto-detect touch devices and set zoom type accordingly
        // Touch devices (mobile, tablets) -> pinch zoom
        // Non-touch devices (desktop) -> mouse zoom
        if (this.isTouchDevice()) {
            this.zoomType = 'pinch';
        } else {
            this.zoomType = 'mouse';
        }

        // Initialize image from array or single URL
        this.initializeImages();

        // Ensure we have at least one image to display
        if (!this.imageUrlArray || this.imageUrlArray.length === 0) {
            if (this.imageUrl) {
                this.imageUrlArray = [this.imageUrl];
            }
        }

    }

    ngAfterViewInit() {
        // Library handles initialization
    }

    ionViewWillLeave() {
        // Restore original status bar style
        this.restoreStatusBar();
        this.unbindGlobalContextMenuBlockers();
    }

    ngOnDestroy() {
        this.unbindGlobalContextMenuBlockers();
    }

    private bindGlobalContextMenuBlockers() {
        this.unbindGlobalContextMenuBlockers();

        const contextMenuHandler = (event: Event) => {
            event.preventDefault();
            event.stopPropagation();
        };
        const mouseDownHandler = (event: MouseEvent) => {
            if (event.button === 2) {
                event.preventDefault();
                event.stopPropagation();
            }
        };

        document.addEventListener('contextmenu', contextMenuHandler, true);
        document.addEventListener('mousedown', mouseDownHandler, true);

        this.removeContextMenuListener = () =>
            document.removeEventListener('contextmenu', contextMenuHandler, true);
        this.removeMouseDownListener = () =>
            document.removeEventListener('mousedown', mouseDownHandler, true);
    }

    private unbindGlobalContextMenuBlockers() {
        this.removeContextMenuListener?.();
        this.removeMouseDownListener?.();
        this.removeContextMenuListener = undefined;
        this.removeMouseDownListener = undefined;
    }

    private async setStatusBarBlack() {
        if (Capacitor.isNativePlatform() && !this.statusBarSet) {
            try {
                // Store current status bar style (we'll restore to Light with #f1f5f5)
                // Since we know the default from app.component, we can just set it directly
                this.originalStatusBarStyle = Style.Light;
                this.originalStatusBarColor = '#f1f5f5';

                // Set status bar to black with light content (white text/icons)
                await StatusBar.setBackgroundColor({ color: '#000000' });
                this.statusBarSet = true;
            } catch (error) {
                console.error('Error setting status bar to black:', error);
            }
        }
    }

    private async restoreStatusBar() {
        if (Capacitor.isNativePlatform() && this.originalStatusBarStyle !== null && this.statusBarSet) {
            try {
                await StatusBar.setStyle({ style: this.originalStatusBarStyle });
                if (this.originalStatusBarColor) {
                    await StatusBar.setBackgroundColor({ color: this.originalStatusBarColor });
                }
                this.statusBarSet = false;
            } catch (error) {
                console.error('Error restoring status bar:', error);
            }
        }
    }

    private initializeImages() {
        if (this.imageUrlArray && this.imageUrlArray.length > 0) {
            // Validate and set initial index
            const validIndex = this.validateImageIndex(this.initialImageIndex);
            this.currentImageIndex = validIndex;
            this.imageUrl = this.imageUrlArray[this.currentImageIndex];
        } else if (this.imageUrl) {
            // If only single imageUrl is provided, use it
            this.imageUrlArray = [this.imageUrl];
            this.currentImageIndex = 0;
        }
    }

    private validateImageIndex(index: number): number {
        if (!this.imageUrlArray || this.imageUrlArray.length === 0) {
            return 0;
        }
        // Ensure index is within valid range
        if (index < 0) {
            return 0;
        }
        if (index >= this.imageUrlArray.length) {
            return this.imageUrlArray.length - 1;
        }
        return index;
    }

    private isTouchDevice(): boolean {
        // Check if running on native mobile (Capacitor)
        if (Capacitor.isNativePlatform()) {
            return true;
        }

        // Check for touch support in browser
        return (
            'ontouchstart' in window ||
            navigator.maxTouchPoints > 0 ||
            (navigator as any).msMaxTouchPoints > 0
        );
    }



    isNativePlatform(): boolean {
        return Capacitor.isNativePlatform();
    }

    shareImage = async () => {
        if (this.disableDownload) {
            return;
        }
        if (!Capacitor.isNativePlatform()) {
            return;
        }

        const loader = await this.loadingController.create({
            message: 'Preparing image...'
        });

        loader.present();

        try {
            const imageUrl = ImageUtil.getImageSource(this.imageUrl);

            // Generate custom filename: userName-sidName-photoName
            const filename = this.generateFileName();

            // Download the image with custom filename
            const uri = await this.downloadImageWithCustomName(imageUrl, filename);
            loader.dismiss();

            if (uri && uri.length > 0) {
                // Use Share plugin to show native share sheet
                // This includes save option in the share sheet
                await Share.share({
                    title: 'Share Image',
                    url: uri,
                    dialogTitle: 'Share or save image'
                });
            } else {
                loader.dismiss();
                this.displayService.toast({
                    message: 'Failed to prepare image'
                });
            }
        } catch (error) {
            loader.dismiss();
        }
    };

    private generateFileName(): string {
        const parts: string[] = [];

        // Add user name (sanitized)
        if (this.userName) {
            parts.push(this.sanitizeFileName(this.userName));
        }

        // Add SID name (sanitized)
        if (this.sidName) {
            parts.push(this.sanitizeFileName(this.sidName));
        }

        // Add photo name (sanitized) or default to current image index
        if (this.photoName) {
            parts.push(this.sanitizeFileName(this.photoName));
        } else {
            // Use index-based name if no photo name provided
            const photoIndex = this.currentImageIndex + 1;
            parts.push(`photo-${photoIndex}`);
        }

        // Join with hyphens and add extension
        const baseName = parts.join('-') || 'image';
        return `${baseName}.jpg`;
    }

    private sanitizeFileName(name: string): string {
        // Remove special characters and replace spaces with hyphens
        return name
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
            .replace(/\s+/g, '-') // Replace spaces with hyphens
            .replace(/-+/g, '-') // Replace multiple hyphens with single
            .trim();
    }

    private async ensureImageLoaded(imageUrl: string): Promise<void> {
        try {
            await new Promise<void>((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => resolve();
                img.onerror = () => reject(new Error('Failed to load image'));
                img.src = imageUrl;
            });
        } catch (error) {
            console.error('ensureImageLoaded failed for', imageUrl, error);
            this.displayService.toast({
                message: 'Image failed to load',
                color: 'danger'
            });
            throw error;
        }
    }

    private async downloadImageWithCustomName(imageUrl: string, filename: string): Promise<string> {
        if (!imageUrl) {
            return '';
        }

        try {
            // Ensure image is fully loaded before downloading
            await this.ensureImageLoaded(imageUrl);

            if (Capacitor.isNativePlatform()) {
                // Use Filesystem API for native platforms
                const base64 = await ImageUtil.urlToBase64(imageUrl);
                const permissionStatus = await Filesystem.checkPermissions();
                if (permissionStatus.publicStorage !== 'granted') {
                    await Filesystem.requestPermissions();
                }
                const res = await Filesystem.writeFile({
                    path: filename,
                    data: base64,
                    directory: Directory.Documents,
                });
                return res.uri;
            } else {
                // Use browser's native download for web/desktop
                const image = await fetch(imageUrl);
                const imageBlob = await image.blob();
                const dataUrl = URL.createObjectURL(imageBlob);

                const link = document.createElement('a');
                document.body.appendChild(link);
                link.setAttribute('href', dataUrl);
                link.setAttribute('download', filename);
                link.click();
                link.remove();

                // Clean up the object URL
                URL.revokeObjectURL(dataUrl);

                return 'downloaded'; // Return a non-empty string to indicate success
            }
        } catch (error) {
            console.error('Error downloading image:', error);
            return '';
        }
    }

    shareToLocal = async () => {
        if (this.disableDownload) {
            return;
        }
        const loader = await this.loadingController.create({
            message: 'Downloading'
        });

        loader.present();

        try {
            const imageUrl = ImageUtil.getImageSource(this.imageUrl);

            // Generate custom filename: userName-sidName-photoName
            const filename = this.generateFileName();

            // Download the image with custom filename
            const uri = await this.downloadImageWithCustomName(imageUrl, filename);
            loader.dismiss();

            if (uri && uri.length > 0) {
                this.displayService.toast({
                    message: 'Image Saved'
                });

                if (Capacitor.isNativePlatform()) {
                    this.showOpenImageOption(uri);
                }
            }
        } catch (error) {
            loader.dismiss();
            console.error('Error downloading image:', error);
            this.displayService.toast({
                message: 'Failed to download image',
                color: 'danger'
            });
        }
    };

    private async showOpenImageOption(uri: string) {
        const actionSheet = await this.actionSheetController.create({
            header: 'Image Downloaded',
            buttons: [
                {
                    text: 'Open Image',
                    icon: 'open-outline',
                    handler: () => {
                        this.openImage(uri);
                    }
                },
                {
                    text: 'Done',
                    icon: 'checkmark-outline',
                    role: 'cancel'
                }
            ]
        });
        await actionSheet.present();
    }

    private async openImage(uri: string) {
        try {
            // The Share plugin can open local files directly
            await Share.share({
                url: uri,
                dialogTitle: 'Open Image With',
            });
        } catch (error) {
            this.displayService.toast({
                message: 'Could not open image.',
                color: 'danger'
            });
        }
    }

    zoomImage(event) {

        const zoomResult = this.zoomResult.nativeElement;
        const mainImage = this.mainImage.nativeElement

        zoomResult.style.visibility = "visible";
        const pos = this.getCursorPos(event);

        const xPercent = (pos.x / mainImage.width) * 100;
        const yPercent = (pos.y / mainImage.height) * 100;

        zoomResult.style.backgroundImage = `url(${mainImage.src})`;
        zoomResult.style.backgroundPosition = `${xPercent}% ${yPercent}%`;
    }

    onFocusOut() {
        const zoomResult = this.zoomResult.nativeElement;
        zoomResult.style.visibility = "hidden";
    }

    getCursorPos(e) {
        const mainImage = this.mainImage.nativeElement
        const rect = mainImage.getBoundingClientRect();
        return {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        };
    }

    getUrl(imageUrl: string = '') {
        return this.autoBase ? ImageUtil.getImageSource(imageUrl || this.imageUrl) : imageUrl || this.imageUrl;
    }





    isCurrentImage(index: number): boolean {
        return index === this.currentImageIndex;
    }

    back() {
        this.modalController.dismiss();
    }

    navigateToImage(index: number) {
        if (!this.imageUrlArray || this.imageUrlArray.length <= 1) return;

        const newIndex = this.validateImageIndex(index);
        if (newIndex === this.currentImageIndex) {
            return;
        }

        // Update index
        this.currentImageIndex = newIndex;
        this.imageUrl = this.imageUrlArray[this.currentImageIndex];
    }

    onImageError(event: Event) {
        console.error('Image failed to load:', event);
        const img = event.target as HTMLImageElement;
        console.error('Failed image src:', img.src);
    }

    onImageLoad(event: Event) {

    }

    preventContextMenu(event: Event) {
        if (!this.disableContextMenu) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
    }

    preventRightClick(event: MouseEvent) {
        if (!this.disableContextMenu || event.button !== 2) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
    }
}

